import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Save, X } from "lucide-react";

interface EditAwardProps {}

interface AwardFormData {
  awardContent: string;
  collective: string;
  individual: string;
  awardTime: string;
  awardFund: string;
  fundSource: string;
  evidence: string;
  basis: string;
  notes: string;
  attachedBasisFiles: File[];
  evidenceFiles: File[];
  saveToHistory: boolean;
}

const EditAward: React.FC<EditAwardProps> = () => {
  const navigate = useNavigate();

  // Get ID from URL path
  const getAwardId = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const id = getAwardId();

  const [formData, setFormData] = useState<AwardFormData>({
    awardContent: "",
    collective: "",
    individual: "",
    awardTime: "2025-09-10",
    awardFund: "",
    fundSource: "",
    evidence: "",
    basis: "",
    notes: "",
    attachedBasisFiles: [],
    evidenceFiles: [],
    saveToHistory: false,
  });

  // Mock data for editing - in real app, this would come from API
  const mockAwardData: AwardFormData = {
    awardContent: "Bằng khen của Bộ trưởng Bộ Khoa học và Công nghệ",
    collective: "Viện Khoa học Thủy lợi Việt Nam",
    individual: "Nguyễn Văn A",
    awardTime: "2023-12-15",
    awardFund: "10.000.000 đ",
    fundSource: "Ngân sách nhà nước",
    evidence: "Bằng khen gốc",
    basis: "Quyết định số 123/QĐ-BKHCN",
    notes: "Thành tích xuất sắc trong nghiên cứu khoa học",
    attachedBasisFiles: [],
    evidenceFiles: [],
    saveToHistory: true,
  };

  useEffect(() => {
    // Load award data for editing
    if (id) {
      // In real app, fetch data from API based on id
      setFormData(mockAwardData);
    }
  }, [id]);

  const handleInputChange = (
    field: keyof AwardFormData,
    value: string | boolean | File[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange =
    (field: "attachedBasisFiles" | "evidenceFiles") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      const currentFiles = formData[field] as File[];
      setFormData((prev) => ({
        ...prev,
        [field]: [...currentFiles, ...files],
      }));
    };

  const removeFile = (
    field: "attachedBasisFiles" | "evidenceFiles",
    index: number
  ) => {
    const currentFiles = formData[field] as File[];
    const updatedFiles = currentFiles.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      [field]: updatedFiles,
    }));
  };

  const handleSave = () => {
    console.log("Updating award:", formData);
    // TODO: Implement update functionality
    navigate("/awards");
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <PageShell title="Sửa Khen thưởng">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Sửa Khen thưởng
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-6">
            {/* Row 1: Nội dung khen thưởng (2 cột) - Tập thể - Cá nhân */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nội dung khen thưởng <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.awardContent}
                  onChange={(e) =>
                    handleInputChange("awardContent", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập nội dung khen thưởng..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tập thể
                </label>
                <select
                  value={formData.collective}
                  onChange={(e) =>
                    handleInputChange("collective", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn -</option>
                  <option>Viện Khoa học Thủy lợi Việt Nam</option>
                  <option>Viện Kỹ thuật Biển</option>
                  <option>Phòng Vật liệu</option>
                  <option>Khoa CNTT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cá nhân
                </label>
                <select
                  value={formData.individual}
                  onChange={(e) =>
                    handleInputChange("individual", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn tác giả -</option>
                  <option>Nguyễn Văn A</option>
                  <option>Trần Thị B</option>
                  <option>Lê Văn C</option>
                  <option>Phạm Thị D</option>
                </select>
              </div>
            </div>

            {/* Row 2: TG nhận khen thưởng - Kinh phí khen thưởng - Nguồn kinh phí - Minh chứng */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TG nhận khen thưởng
                </label>
                <input
                  type="date"
                  value={formData.awardTime}
                  onChange={(e) =>
                    handleInputChange("awardTime", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kinh phí khen thưởng
                </label>
                <input
                  type="text"
                  value={formData.awardFund}
                  onChange={(e) =>
                    handleInputChange("awardFund", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số tiền..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nguồn kinh phí <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.fundSource}
                  onChange={(e) =>
                    handleInputChange("fundSource", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn -</option>
                  <option>Ngân sách nhà nước</option>
                  <option>Ngân sách địa phương</option>
                  <option>Tự có</option>
                  <option>Doanh nghiệp tài trợ</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minh chứng
                </label>
                <input
                  type="text"
                  value={formData.evidence}
                  onChange={(e) =>
                    handleInputChange("evidence", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập minh chứng..."
                />
              </div>
            </div>

            {/* Row 3: Căn cứ - Ghi chú */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Căn cứ
                </label>
                <textarea
                  value={formData.basis}
                  onChange={(e) => handleInputChange("basis", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập căn cứ pháp lý..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập ghi chú..."
                />
              </div>
            </div>

            {/* Row 4: File uploads */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Căn cứ đính kèm
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      onChange={handleFileChange("attachedBasisFiles")}
                      className="hidden"
                      id="basis-file-upload"
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      multiple
                    />
                    <label
                      htmlFor="basis-file-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                    >
                      Choose Files
                    </label>
                    <span className="text-sm text-gray-500">
                      {formData.attachedBasisFiles.length > 0
                        ? `${formData.attachedBasisFiles.length} file${
                            formData.attachedBasisFiles.length > 1 ? "s" : ""
                          } chosen`
                        : "No file chosen"}
                    </span>
                  </div>
                  {formData.attachedBasisFiles.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                      {formData.attachedBasisFiles.map((file, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeFile("attachedBasisFiles", index)
                              }
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tài liệu minh chứng
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      onChange={handleFileChange("evidenceFiles")}
                      className="hidden"
                      id="evidence-file-upload"
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      multiple
                    />
                    <label
                      htmlFor="evidence-file-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                    >
                      Choose Files
                    </label>
                    <span className="text-sm text-gray-500">
                      {formData.evidenceFiles.length > 0
                        ? `${formData.evidenceFiles.length} file${
                            formData.evidenceFiles.length > 1 ? "s" : ""
                          } chosen`
                        : "No file chosen"}
                    </span>
                  </div>
                  {formData.evidenceFiles.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                      {formData.evidenceFiles.map((file, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile("evidenceFiles", index)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 5: Checkbox - Highlighted */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="saveToHistory"
                  checked={formData.saveToHistory}
                  onChange={(e) =>
                    handleInputChange("saveToHistory", e.target.checked)
                  }
                  className="w-4 h-4 rounded border-2 border-blue-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mr-3"
                />
                <label
                  htmlFor="saveToHistory"
                  className="text-sm font-medium text-blue-800 cursor-pointer"
                >
                  ✓ Lưu vào lí lịch?
                </label>
              </div>
              <p className="text-xs text-blue-600 mt-1 ml-7">
                Thông tin này sẽ được lưu vào hồ sơ cá nhân của bạn
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default EditAward;
